import { useEffect, useState, useRef } from "react";
import { Target, Sprout, LineChart, Cpu, Settings2, Plus, Wallet } from "lucide-react";
import { LiveSignalsPanel } from "@/components/LiveSignalsPanel";
import { NurseryPanel } from "@/components/NurseryPanel";
import { DashboardViewport } from "@/components/DashboardViewport";
import { HistoryPanel } from "@/components/HistoryPanel";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { WalletTab } from "@/components/WalletTab";
import { useApp } from "@/context/AppCtx";
import { useAutoPilot } from "@/hooks/useAutoPilot";
import type { MobileTab } from "@/context/types";

const TABS: { id: MobileTab | "deploy" | "wallet"; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "wallet",  label: "Wallet",  Icon: Wallet },
  { id: "signals", label: "Signals", Icon: Target },
  { id: "nursery", label: "Nursery", Icon: Sprout },
  { id: "deploy",  label: "Deploy",  Icon: Plus },
  { id: "chart",   label: "Scope", Icon: LineChart },
  { id: "history", label: "Agent",   Icon: Cpu },
  { id: "setup",   label: "Setup",   Icon: Settings2 },
];

export function MobileShell() {
  useAutoPilot();
  const { setCaMintInput, mobileActiveTab, setMobileActiveTab } = useApp() as any;
  const lastScrollY = useRef(0);
  const [navVisible, setNavVisible] = useState(true);

  function handleOpenToken(mint: string) {
    setCaMintInput(mint);
    setMobileActiveTab("chart");
    window.history.pushState({ token: mint }, "");
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const currentScrollY = target.scrollTop;
    
    // Only react to significant scrolls to prevent jitter
    if (Math.abs(currentScrollY - lastScrollY.current) < 10) return;

    if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
      // Scrolling down
      if (navVisible && mobileActiveTab !== "chart") {
        setNavVisible(false);
        document.body.classList.add("nav-hidden");
      }
    } else {
      // Scrolling up
      if (!navVisible) {
        setNavVisible(true);
        document.body.classList.remove("nav-hidden");
      }
    }
    lastScrollY.current = currentScrollY;
  };

  // Handle hardware back button
  useEffect(() => {
    const handlePopState = () => {
      if (new URLSearchParams(window.location.search).get("mint")) {
        setMobileActiveTab("chart");
      } else {
        setMobileActiveTab("nursery");
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [setMobileActiveTab]);

  useEffect(() => {
    if (mobileActiveTab === "chart") {
      setNavVisible(true);
      document.body.classList.remove("nav-hidden");
    }
  }, [mobileActiveTab]);

  // Clean up global class on unmount
  useEffect(() => {
    return () => document.body.classList.remove("nav-hidden");
  }, []);

  // NurseryPanel dispatches "open-token" custom event — listen globally
  useEffect(() => {
    const handler = (e: Event) => {
      const mint = (e as CustomEvent<string>).detail;
      if (mint) handleOpenToken(mint);
    };
    window.addEventListener("open-token", handler);
    return () => window.removeEventListener("open-token", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mobile-shell" onScrollCapture={handleScroll}>
      {/* Main content area — one panel at a time, full height */}
      <div className="mobile-shell__content">
        {mobileActiveTab === "wallet" && (
          <WalletTab />
        )}
        {mobileActiveTab === "signals" && (
          <LiveSignalsPanel onOpenToken={handleOpenToken} />
        )}
        {mobileActiveTab === "nursery" && (
          <NurseryPanel onOpenToken={handleOpenToken} />
        )}
        {mobileActiveTab === "chart" && (
          <DashboardViewport />
        )}
        {mobileActiveTab === "history" && (
          <HistoryPanel />
        )}
        {mobileActiveTab === "setup" && (
          <div className="h-full overflow-y-auto">
            <DashboardSidebar />
          </div>
        )}
      </div>

      {/* Bottom tab bar */}
      <nav id="mobile-bottom-nav" className="mobile-tab-bar" role="tablist" aria-label="Main navigation">
        {TABS.map(({ id, label, Icon }) => {
          if (id === "deploy") {
            return (
              <button
                key={id}
                onClick={() => {
                  window.history.pushState({}, "", "/deploy");
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }}
                className="mobile-tab-bar__item flex-col group"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-tr from-emerald-500 to-emerald-400 text-white shadow-md shadow-emerald-500/20 group-hover:scale-110 transition-transform">
                  <Icon className="size-5" />
                </div>
                <span className="mobile-tab-bar__label mt-1">{label}</span>
              </button>
            );
          }

          const isActive = mobileActiveTab === id;
          return (
            <button
              key={id}
              role="tab"
              aria-selected={isActive}
              aria-label={label}
              onClick={() => setMobileActiveTab(id)}
              className={`mobile-tab-bar__item ${isActive ? "mobile-tab-bar__item--active" : ""}`}
            >
              {/* Active glow dot */}
              {isActive && <span className="mobile-tab-bar__dot" aria-hidden />}
              <Icon
                className={`mobile-tab-bar__icon ${isActive ? "mobile-tab-bar__icon--active" : ""}`}
              />
              <span className={`mobile-tab-bar__label ${isActive ? "mobile-tab-bar__label--active" : ""}`}>
                {label}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
